import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { AlertCircle, CheckCircle2, ExternalLink, Lock, X } from 'lucide-react';
import { isHttpUrl, UUID_RE, type FailedStatus, type LinkError } from '../protocol';
import GencoreMark from '../brand/GencoreMark';
import { getCheckoutSession, getLinkInfo, startPayment, type ApiResult, type CheckoutSession, type LinkInfo, type PayResult } from './api';
import { useArming } from './arming';
import { countryOptions, defaultCountry } from './countries';
import { formatMoney } from './format';
import type { ParentChannel } from './parent';
import { startPoller } from './poller';
import { closePopup, openBlankPopup, sendPopupTo } from './popup';
import { canFrameProvider, isAllowedProviderUrl } from './urlSafety';

type ResultStatus = 'succeeded' | FailedStatus;

type View =
  | { kind: 'loading' }
  | { kind: 'link_error'; error: LinkError; message: string }
  | { kind: 'form' }
  | {
      kind: 'waiting';
      transactionId: string;
      token: string;
      url: string;
      presentation: 'popup' | 'link' | 'frame';
      processing: boolean;
    }
  | { kind: 'result'; status: ResultStatus; transactionId: string; returnUrl: string | null };

const NOT_ACCEPTING = 'This link is no longer accepting payments';
const OUTDATED_API = 'This Gencoin API is older than the embedded checkout. Deploy the API changes first.';

function linkErrorView(res: ApiResult<LinkInfo>): View {
  if (res.ok) {
    const info = res.data;
    if (info.checkoutApiVersion === undefined || info.checkoutApiVersion === null) {
      return { kind: 'link_error', error: 'api_outdated', message: OUTDATED_API };
    }
    return { kind: 'link_error', error: 'unavailable', message: NOT_ACCEPTING };
  }
  if (res.status === 404) return { kind: 'link_error', error: 'not_found', message: "This payment link doesn't exist." };
  return { kind: 'link_error', error: 'error', message: "We couldn't load this checkout. Please try again in a moment." };
}

function payErrorMessage(res: Extract<ApiResult<PayResult>, { ok: false }>): string {
  if (res.status === 404 || res.status === 410) return NOT_ACCEPTING;
  if (res.status === 409 && ['INACTIVE', 'EXHAUSTED', 'MERCHANT_UNAVAILABLE'].includes(res.code ?? '')) return NOT_ACCEPTING;
  if (res.status === 429) return 'Too many attempts, please wait a moment';
  if (res.status === 400 && res.message) return res.message;
  return 'Could not start the payment. Please try again.';
}

function randomHex(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return 'the shop';
  }
}

const RESULT_COPY: Record<ResultStatus, { title: string; body: string }> = {
  succeeded: { title: 'Payment received', body: 'Thank you. The merchant has been notified.' },
  failed: { title: 'Payment failed', body: 'The payment did not go through. No money was taken.' },
  cancelled: { title: 'Payment cancelled', body: 'You cancelled the payment. No money was taken.' },
  expired: { title: 'This checkout expired', body: 'The payment window timed out before it was completed.' },
};

export default function App({ slug, channel }: { slug: string; channel: ParentChannel }) {
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [country, setCountry] = useState(() => defaultCountry(navigator.language));
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<Window | null>(null);
  const readySent = useRef(false);

  const testMode = info?.testMode === true;
  const armed = useArming(cardRef, info !== null && view.kind !== 'loading' && view.kind !== 'link_error');
  const countries = useMemo(() => countryOptions(navigator.language), []);

  // Link info.
  useEffect(() => {
    let cancelled = false;
    void getLinkInfo(slug).then((res) => {
      if (cancelled) return;
      if (res.ok && res.data.checkoutApiVersion != null && res.data.isActive && !res.data.isExpired) {
        setInfo(res.data);
        setView({ kind: 'form' });
      } else {
        if (res.ok) setInfo(null);
        setView(linkErrorView(res));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // checkout.ready, once, after the card or the link error has rendered.
  useEffect(() => {
    if (readySent.current || view.kind === 'loading') return;
    readySent.current = true;
    channel.post('checkout.ready', {
      testMode,
      linkError: view.kind === 'link_error' ? view.error : null,
    });
  }, [view, testMode, channel]);

  // checkout.resize: the overlay follows the card's height.
  useEffect(() => {
    const el = cardRef.current;
    if (!channel.enabled || !el || typeof ResizeObserver !== 'function') return;
    let frame = 0;
    let last = -1;
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const height = Math.min(4000, Math.max(0, Math.ceil(el.getBoundingClientRect().height)));
        if (height !== last) {
          last = height;
          channel.post('checkout.resize', { height });
        }
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [channel]);

  // Escape inside the frame closes the overlay, like Escape on the parent does.
  useEffect(() => {
    if (!channel.enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') channel.post('checkout.close', { reason: 'escape' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [channel]);

  const finish = useCallback(
    (status: ResultStatus, transactionId: string, returnUrl: string | null) => {
      closePopup(popupRef.current);
      popupRef.current = null;
      const safeReturn = returnUrl && isHttpUrl(returnUrl) ? returnUrl : null;
      if (status === 'succeeded') {
        channel.post('checkout.payment_succeeded', { transactionId, status, returnUrl: safeReturn });
      } else {
        channel.post('checkout.payment_failed', { transactionId, status });
      }
      setView({ kind: 'result', status, transactionId, returnUrl: safeReturn });
    },
    [channel]
  );

  // Status polling while waiting for the provider.
  const waitingToken = view.kind === 'waiting' ? view.token : null;
  const waitingTx = view.kind === 'waiting' ? view.transactionId : null;
  useEffect(() => {
    if (!waitingToken || !waitingTx) return;
    return startPoller({
      fetchSession: () => getCheckoutSession(waitingToken),
      onSession: (s: CheckoutSession) => {
        const tx = UUID_RE.test(s.transactionId) ? s.transactionId : waitingTx;
        switch (s.status) {
          case 'succeeded':
            finish('succeeded', tx, s.returnUrl);
            return true;
          case 'failed':
          case 'refunded':
            finish('failed', tx, null);
            return true;
          case 'cancelled':
            finish('cancelled', tx, null);
            return true;
          case 'expired':
            finish('expired', tx, null);
            return true;
          case 'processing':
            setView((v) => (v.kind === 'waiting' && !v.processing ? { ...v, processing: true } : v));
            return false;
          default:
            return s.final;
        }
      },
      onGiveUp: () => finish('expired', waitingTx, null),
    });
  }, [waitingToken, waitingTx, finish]);

  // Pay re-arms when a 429 cooldown ends.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const t = setTimeout(() => forceTick((n) => n + 1), cooldownUntil - Date.now());
    return () => clearTimeout(t);
  }, [cooldownUntil]);
  const coolingDown = cooldownUntil > Date.now();

  async function pay(popup: Window | null): Promise<void> {
    const res = await startPayment(slug, { paymentMethod: 'credit_card', customerCountry: country });
    if (!res.ok) {
      closePopup(popup);
      popupRef.current = null;
      if (res.status === 429) setCooldownUntil(Date.now() + (res.retryAfterSec ?? 10) * 1000);
      setPayError(payErrorMessage(res));
      setView({ kind: 'form' });
      setBusy(false);
      return;
    }

    const data = res.data;
    const target = data.checkout ?? (data.redirectUrl ? { mode: 'redirect' as const, url: data.redirectUrl } : null);
    const token = data.checkoutSession?.token;
    const transactionId = data.transactionId;
    if (!target || !token || !transactionId || !UUID_RE.test(transactionId) || !isAllowedProviderUrl(target.url, testMode)) {
      closePopup(popup);
      popupRef.current = null;
      setPayError('Could not start the payment. Please try again.');
      setView({ kind: 'form' });
      setBusy(false);
      return;
    }

    // Switch on the mode the API chose, never on the provider's name. The
    // frame never navigates itself or the top window: providers get a popup,
    // a link, or (embed mode on an allow-listed origin only) a nested frame.
    let presentation: 'popup' | 'link' | 'frame';
    if (target.mode === 'embed' && canFrameProvider(target.url)) {
      closePopup(popup);
      popupRef.current = null;
      presentation = 'frame';
    } else if (sendPopupTo(popup, target.url)) {
      presentation = 'popup';
    } else {
      closePopup(popup);
      popupRef.current = null;
      presentation = 'link';
    }

    channel.post('checkout.payment_started', { transactionId });
    setView({ kind: 'waiting', transactionId, token, url: target.url, presentation, processing: false });
    setBusy(false);
  }

  function onPayClick(e: ReactMouseEvent<HTMLButtonElement>): void {
    // A synthetic click (el.click(), dispatchEvent) from a framing page or an
    // extension is never a customer's decision to pay.
    if (!e.nativeEvent.isTrusted || !armed || busy || coolingDown || !info) return;
    setBusy(true);
    setPayError(null);
    // Opened here, synchronously in the trusted click, or the browser blocks it.
    const popup = openBlankPopup(`gencoin_pay_${channel.sid ?? randomHex()}`);
    popupRef.current = popup;
    void pay(popup);
  }

  function onBack(): void {
    closePopup(popupRef.current);
    popupRef.current = null;
    setView({ kind: 'form' });
  }

  const closeButton =
    channel.enabled ? (
      <button
        type="button"
        data-testid="gc-close"
        aria-label="Close checkout"
        onClick={() => channel.post('checkout.close', { reason: 'user' })}
        className="absolute right-3 top-3 rounded-pill p-1.5 text-ui-mute hover:bg-ui-page hover:text-ui-ink"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
    ) : null;

  const amount = info?.amountFiat ? formatMoney(info.amountFiat, info.fiatCurrency) : null;

  return (
    <main className={channel.embedded ? 'bg-ui-page' : 'flex min-h-screen items-start justify-center bg-ui-page px-4 py-10 sm:items-center'}>
      <div
        ref={cardRef}
        data-testid="gc-card"
        className={
          channel.embedded
            ? 'relative w-full bg-surface px-6 pb-6 pt-5'
            : 'relative w-full max-w-[440px] rounded-card bg-surface px-6 pb-6 pt-5 shadow-float'
        }
      >
        {closeButton}
        <header className="mb-4 flex items-center gap-2 pr-10">
          <GencoreMark className="h-7 w-auto" />
          <span className="font-display text-base font-bold text-ui-ink">Gencoin</span>
          <span className="ml-1 inline-flex items-center gap-1 text-xs text-ui-mute">
            <Lock className="h-3 w-3" aria-hidden /> Secure checkout
          </span>
        </header>

        {testMode && (
          <p data-testid="gc-sandbox" className="mb-4 rounded-lg bg-gc-warm-bg px-3 py-2 text-sm font-medium text-gc-warm-ink">
            Sandbox: payments here are simulated. No real money moves.
          </p>
        )}

        {view.kind === 'loading' && <p className="py-10 text-center text-ui-mute">Loading checkout…</p>}

        {view.kind === 'link_error' && (
          <div data-testid="gc-error" role="alert" className="flex items-start gap-2 rounded-lg bg-ui-danger-bg px-3 py-3 text-ui-danger">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <p>{view.message}</p>
          </div>
        )}

        {info && view.kind !== 'loading' && view.kind !== 'link_error' && (
          <section className="mb-5">
            <p data-testid="gc-amount" className="font-display text-4xl font-extrabold tracking-tight text-ui-ink">
              {amount ?? info.fiatCurrency}
            </p>
            {info.description && (
              <p data-testid="gc-description" className="mt-1 text-ui-mute">
                {info.description}
              </p>
            )}
          </section>
        )}

        {info && view.kind === 'form' && (
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-ui-ink">Country</span>
              <select
                data-testid="gc-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-lg border border-field-line bg-surface px-3 py-2.5 text-base text-ui-ink sm:text-sm"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {payError && (
              <p data-testid="gc-error" role="alert" className="rounded-lg bg-ui-danger-bg px-3 py-2 text-sm text-ui-danger">
                {payError}
              </p>
            )}
            <button
              type="button"
              data-testid="gc-pay"
              disabled={!armed || busy || coolingDown}
              aria-busy={busy}
              onClick={onPayClick}
              className="w-full rounded-pill bg-gc-gradient-strong px-6 py-3.5 font-display text-base font-bold text-white shadow-button disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Starting payment…' : amount ? `Pay ${amount}` : 'Pay'}
            </button>
            <p className="text-center text-xs text-ui-mute">You'll finish paying on our provider's secure page.</p>
          </div>
        )}

        {view.kind === 'waiting' && (
          <div data-testid="gc-waiting" className="space-y-4">
            {view.presentation === 'frame' ? (
              <iframe
                title="Secure payment"
                src={view.url}
                allow="payment"
                className="h-[480px] w-full rounded-lg border border-ui-edge"
              />
            ) : view.presentation === 'link' ? (
              <a
                data-testid="gc-open-payment"
                href={view.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-pill bg-gc-gradient-strong px-6 py-3.5 font-display font-bold text-white shadow-button"
              >
                Continue to secure payment page <ExternalLink className="h-4 w-4" aria-hidden />
              </a>
            ) : (
              <>
                <p className="font-medium text-ui-ink">Complete the payment in the window that opened</p>
                <a
                  data-testid="gc-open-payment"
                  href={view.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-gc-blue-ink underline"
                >
                  Didn't see it? Open the payment page <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </>
            )}
            <p className="text-sm text-ui-mute" aria-live="polite">
              {view.processing ? 'Payment received by the provider, confirming…' : 'Waiting for the payment to complete…'}
            </p>
            <button type="button" data-testid="gc-back" onClick={onBack} className="text-sm font-semibold text-gc-blue-ink">
              Back
            </button>
          </div>
        )}

        {view.kind === 'result' && (
          <div data-testid="gc-result" data-status={view.status} className="space-y-4" aria-live="polite">
            <div className="flex items-start gap-2">
              {view.status === 'succeeded' ? (
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-ui-success" aria-hidden />
              ) : (
                <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-ui-danger" aria-hidden />
              )}
              <div>
                <p className="font-display text-lg font-bold text-ui-ink">{RESULT_COPY[view.status].title}</p>
                <p className="text-sm text-ui-mute">{RESULT_COPY[view.status].body}</p>
              </div>
            </div>
            {view.status === 'succeeded' ? (
              channel.enabled ? (
                <button
                  type="button"
                  data-testid="gc-done"
                  onClick={() => channel.post('checkout.close', { reason: 'done' })}
                  className="w-full rounded-pill bg-gc-gradient-strong px-6 py-3.5 font-display font-bold text-white shadow-button"
                >
                  Done
                </button>
              ) : view.returnUrl && !channel.embedded ? (
                <a
                  data-testid="gc-return"
                  href={view.returnUrl}
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center rounded-pill bg-gc-gradient-strong px-6 py-3.5 font-display font-bold text-white shadow-button"
                >
                  Continue to {hostnameOf(view.returnUrl)}
                </a>
              ) : (
                <p className="text-sm text-ui-mute">You can close this {channel.embedded ? 'window' : 'tab'}.</p>
              )
            ) : (
              <button
                  type="button"
                  data-testid="gc-retry"
                  disabled={!armed || busy || coolingDown}
                  onClick={onPayClick}
                  className="w-full rounded-pill bg-gc-gradient-strong px-6 py-3.5 font-display font-bold text-white shadow-button disabled:opacity-60"
                >
                  {busy ? 'Starting payment…' : 'Try again'}
                </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
